import { LazyFallback } from 'components/shared/LazyFallback';
import { ProductAndClientSelector } from 'components/shared/ProductAndClientSelector';
import { ShortcutCheatSheetBanner } from 'components/Workspaces/Batch/ShortcutCheatSheetBanner';
import type { SPIntl } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch } from 'state/state.common';
import { batchWorkspaceTabProductSelectionSubmittedThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';
import { camelCaseToWords } from 'utils/string';

import { connectedBatchWorkspace } from '../BatchWorkspace.connect';
import type { BatchWorkspaceProps } from '../BatchWorkspace.models';
import { BatchFormGrid } from '../FormGrid/BatchFormGrid';
import { BatchToolbar } from '../Toolbar/BatchToolbar';

export const BatchTabContent: FC<BatchWorkspaceProps> = ({
  productSubtype,
  productType,
  wrapper,
  payoffType,
  isProductValidated,
  tabId,
  isFetchingBatchTabContent,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { formatMessage } = useIntl();

  if (isProductValidated && productType && productSubtype && wrapper) {
    return (
      <div className="w-100 h-100 d-flex flex-column align-items-stretch justify-content-start">
        <BatchToolbar tabId={tabId} />
        <BatchFormGrid
          className="flex-grow-1 overflow-auto border"
          tabId={tabId}
          productType={productType}
          wrapper={wrapper}
          payoffTypes={payoffType}
        />
        <ShortcutCheatSheetBanner />
      </div>
    );
  }

  if (isFetchingBatchTabContent) {
    return <LazyFallback />;
  }

  return (
    <ProductAndClientSelector
      workspace="newBatch"
      canSelectClient={false}
      canSelectPayoffType={true}
      onSubmitted={({ productType, productSubType, wrapper, payoffType, requestType }) => {
        const tabName = productSubType ? getProductLabel(formatMessage, productSubType) : '';
        dispatch(
          batchWorkspaceTabProductSelectionSubmittedThunk(
            tabId,
            productType,
            productSubType,
            wrapper,
            requestType,
            payoffType,
            tabName,
          ),
        );
      }}
    />
  );
};

type FormatMessageType = SPIntl['formatMessage'];
function getProductLabel(formatMessage: FormatMessageType, value: string): string {
  return formatMessage({
    id: `quote.products.${value}` as IntlKey,
    defaultMessage: camelCaseToWords(value),
  });
}

export const ConnectedBatchTabContent = connectedBatchWorkspace(BatchTabContent);
